/**
 * Shared Lead Lifecycle UI primitives + a hook that fetches the
 * canonical enums once per session.
 *
 * The exports here are what page-level code uses; they sit on top of
 * the `/api/lead-lifecycle/enums` endpoint so a backend label change
 * propagates without touching the frontend.
 *
 * - `useLifecycleEnums()` — fetch stages / sub-statuses / temperatures
 *                            / lost-reasons. Cached for the session.
 * - `<LeadStageBadge stage sub_status />` — read-only chip.
 * - `<LeadTemperaturePill status onChange />` — click to cycle, or
 *                            pass `readOnly` to render display-only.
 * - `<LeadStagePicker lead onChanged />` — full transition flow:
 *                            includes the lost-reason dialog, the
 *                            reason-required prompt for skips/backwards,
 *                            and posts to /api/leads/{id}/stage.
 * - `<LeadStageHistory leadId />` — audit timeline component.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Activity, ChevronDown, ChevronUp, Flame, History as HistoryIcon,
  CheckCircle2, XCircle, AlertOctagon, Snowflake, Cloud,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Enum context (single fetch per session)

const EnumsContext = createContext(null);

export function LifecycleEnumsProvider({ token, children }) {
  const [enums, setEnums] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/lead-lifecycle/enums?token=${token}`);
        if (alive) setEnums(res.data?.data || null);
      } catch (_) {
        // Degraded mode — components fall back to a minimal local
        // enum list so the UI doesn't break when offline.
        if (alive) setEnums(FALLBACK_ENUMS);
      }
    })();
    return () => { alive = false; };
  }, [token]);
  const value = enums || FALLBACK_ENUMS;
  return <EnumsContext.Provider value={value}>{children}</EnumsContext.Provider>;
}

/** Hook that returns the canonical enums (cached for the session). */
export function useLifecycleEnums() {
  // Allow uncontextualized usage too — pages that haven't wrapped
  // their tree in the provider get the fallback enums instead of
  // crashing with "used outside provider".
  const ctx = useContext(EnumsContext);
  return ctx || FALLBACK_ENUMS;
}

// Minimal enum mirror so the UI still renders if the API can't be
// reached or the provider wasn't mounted. Backend remains the source
// of truth.
const FALLBACK_ENUMS = {
  stages: [
    { key: 'enquiry',     label: 'Enquiry',     sub_statuses: [{ key: '', label: '—' }, { key: 'new', label: 'New' }, { key: 'contact_attempted', label: 'Contact attempted' }, { key: 'reached', label: 'Reached' }, { key: 'broker_verification', label: 'Broker verification' }] },
    { key: 'briefing',    label: 'Briefing',    sub_statuses: [{ key: '', label: '—' }, { key: 'scheduled', label: 'Scheduled' }, { key: 'done', label: 'Done' }, { key: 'no_show', label: 'No-show' }, { key: 'rescheduled', label: 'Rescheduled' }] },
    { key: 'qualified',   label: 'Qualified',   sub_statuses: [{ key: '', label: '—' }, { key: 'in_progress', label: 'In progress' }, { key: 'qualified', label: 'Qualified' }, { key: 'disqualified', label: 'Disqualified' }] },
    { key: 'site_visit',  label: 'Site Visit',  sub_statuses: [{ key: '', label: '—' }, { key: 'scheduled', label: 'Scheduled' }, { key: 'confirmed', label: 'Confirmed' }, { key: 'completed', label: 'Completed' }, { key: 'no_show', label: 'No-show' }, { key: 'rescheduled', label: 'Rescheduled' }] },
    { key: 'negotiation', label: 'Negotiation', sub_statuses: [{ key: '', label: '—' }, { key: 'offer_made', label: 'Offer made' }, { key: 'counter_offer', label: 'Counter-offer' }, { key: 'awaiting_approval', label: 'Awaiting approval' }, { key: 'approved', label: 'Approved' }] },
    { key: 'booking',     label: 'Booking',     sub_statuses: [{ key: '', label: '—' }, { key: 'eoi_received', label: 'EOI received' }, { key: 'booking_form_signed', label: 'Booking form signed' }, { key: 'payment_received', label: 'Payment received' }] },
    { key: 'closed',      label: 'Closed',      sub_statuses: [{ key: '', label: '—' }, { key: 'won', label: 'Won' }, { key: 'lost', label: 'Lost' }, { key: 'dormant', label: 'Dormant' }] },
  ],
  temperatures: [
    { key: 'active',  label: 'Active',  emoji: '🟢' },
    { key: 'hot',     label: 'Hot',     emoji: '🔥' },
    { key: 'warm',    label: 'Warm',    emoji: '🌤️' },
    { key: 'cold',    label: 'Cold',    emoji: '❄️' },
    { key: 'dropped', label: 'Dropped', emoji: '⛔' },
  ],
  lost_reasons: [
    { key: 'budget_mismatch', label: 'Budget mismatch' },
    { key: 'timing',          label: 'Timing' },
    { key: 'competitor',      label: 'Competitor' },
    { key: 'unresponsive',    label: 'Unresponsive' },
    { key: 'other',           label: 'Other (see note)' },
  ],
};

// ---- Stage chip

const STAGE_TONE = {
  enquiry:     'bg-slate-100 text-slate-700 border-slate-300',
  briefing:    'bg-blue-100 text-blue-700 border-blue-300',
  qualified:   'bg-violet-100 text-violet-700 border-violet-300',
  site_visit:  'bg-amber-100 text-amber-700 border-amber-300',
  negotiation: 'bg-orange-100 text-orange-700 border-orange-300',
  booking:     'bg-emerald-100 text-emerald-700 border-emerald-300',
  closed:      'bg-slate-200 text-slate-800 border-slate-400',
};

export function LeadStageBadge({ stage, sub_status, compact = false }) {
  const enums = useLifecycleEnums();
  const stageDef = enums.stages.find((s) => s.key === stage);
  const subDef = stageDef?.sub_statuses.find((ss) => ss.key === (sub_status || ''));
  const label = stageDef?.label || '—';
  const subLabel = subDef && subDef.key ? subDef.label : null;
  return (
    <Badge className={`border ${STAGE_TONE[stage] || STAGE_TONE.enquiry} ${compact ? 'text-[10px]' : 'text-xs'}`}>
      {label}
      {subLabel && <span className="opacity-70 ml-1">· {subLabel}</span>}
    </Badge>
  );
}

// ---- Temperature pill

const TEMP_TONE = {
  active:  'bg-emerald-100 text-emerald-700 border-emerald-300',
  hot:     'bg-red-100 text-red-700 border-red-300',
  warm:    'bg-orange-100 text-orange-700 border-orange-300',
  cold:    'bg-blue-100 text-blue-700 border-blue-300',
  dropped: 'bg-slate-200 text-slate-700 border-slate-400',
};

export function LeadTemperaturePill({ status, onChange, readOnly = false, compact = false }) {
  const enums = useLifecycleEnums();
  const cur = (status || 'active').toLowerCase();
  const def = enums.temperatures.find((t) => t.key === cur) || enums.temperatures[0];
  const [open, setOpen] = useState(false);
  const Body = (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${TEMP_TONE[cur]} ${compact ? 'text-[10px]' : 'text-xs'}`}>
      <span>{def.emoji}</span>
      {def.label}
    </span>
  );
  if (readOnly || !onChange) return Body;
  return (
    <div className="relative inline-block">
      <button type="button" onClick={() => setOpen((v) => !v)} className="focus:outline-none">
        {Body}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-32 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-md z-30">
          {enums.temperatures.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => { onChange(t.key); setOpen(false); }}
              className={`w-full text-left px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-1 ${
                t.key === cur ? 'font-semibold' : ''
              }`}
            >
              <span>{t.emoji}</span> {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Stage picker (full transition flow)

export function LeadStagePicker({ lead, collection = 'auto', onChanged, compact = false }) {
  const enums = useLifecycleEnums();
  const [dialogOpen, setDialogOpen] = useState(false);
  const token = useToken();

  const onPick = (stage) => {
    if (stage === (lead?.stage || 'enquiry')) return;
    setDialogOpen({ targetStage: stage });
  };

  return (
    <>
      <Select
        value={lead?.stage || 'enquiry'}
        onValueChange={onPick}
      >
        <SelectTrigger className={compact ? 'h-7 text-xs' : 'h-8 text-sm'}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {enums.stages.map((s) => (
            <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {dialogOpen && (
        <StageTransitionDialog
          lead={lead}
          collection={collection}
          targetStage={dialogOpen.targetStage}
          token={token}
          onClose={() => setDialogOpen(false)}
          onCompleted={(updated) => {
            setDialogOpen(false);
            onChanged?.(updated);
          }}
        />
      )}
    </>
  );
}

// Helper hook — reads the token from sessionStorage / localStorage.
// We don't import AuthContext here because this component is meant to
// be reusable across pages that may not be wrapped in the same
// provider in all configurations (e.g. modals embedded in inboxes).
function useToken() {
  // Prefer the in-memory token bridged through the provider. Falls
  // back to localStorage so the component still works when
  // mounted outside an AuthProvider tree (e.g. iframe widgets).
  const [tok] = useState(() => {
    try {
      return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    } catch (_) {
      return '';
    }
  });
  return tok;
}

// ---- Transition dialog

function StageTransitionDialog({
  lead, collection, targetStage, token, onClose, onCompleted,
}) {
  const enums = useLifecycleEnums();
  const stageDef = enums.stages.find((s) => s.key === targetStage);
  const [subStatus, setSubStatus] = useState('');
  const [reason, setReason] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [lostNote, setLostNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Derive whether a reason is required: yes when skipping forward by
  // 2+ stages or moving backward, AND always when going to closed.
  const stageOrder = enums.stages.map((s) => s.key);
  const curIdx = stageOrder.indexOf(lead?.stage || 'enquiry');
  const tgtIdx = stageOrder.indexOf(targetStage);
  const isSkip = tgtIdx > curIdx + 1;
  const isBackward = tgtIdx < curIdx;
  const reasonRequired = isSkip || isBackward;
  const isClosing = targetStage === 'closed';
  const isLost = isClosing && subStatus === 'lost';

  // Pre-pick a sensible default sub-status when entering the dialog.
  useEffect(() => {
    if (targetStage === 'closed') setSubStatus('won');  // optimistic default
    else setSubStatus('');
  }, [targetStage]);

  const submit = async () => {
    if (reasonRequired && !reason.trim()) {
      toast.error('A reason is required for this transition');
      return;
    }
    if (isLost && !lostReason) {
      toast.error('Pick a lost reason');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.patch(
        `${API}/leads/${lead.id}/stage?token=${token}`,
        {
          new_stage: targetStage,
          sub_status: subStatus || null,
          collection,
          reason: reason.trim() || null,
          lost_reason: isLost ? lostReason : null,
          lost_reason_note: isLost ? lostNote : null,
        },
      );
      toast.success(`Moved to ${stageDef?.label || targetStage}`);
      onCompleted?.(res.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not move lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Move to {stageDef?.label}{' '}
            {isSkip && <span className="text-amber-600 text-xs">(skipping stages)</span>}
            {isBackward && <span className="text-amber-600 text-xs">(moving backward)</span>}
          </DialogTitle>
          <DialogDescription>
            {isSkip && 'You are skipping one or more stages. A brief reason is required for the audit trail.'}
            {isBackward && 'You are moving the lead backward. A brief reason is required for the audit trail.'}
            {!reasonRequired && 'Confirm the sub-status if applicable.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {stageDef?.sub_statuses?.length > 1 && (
            <div>
              <label className="text-xs text-slate-600">Sub-status</label>
              <Select value={subStatus || ''} onValueChange={setSubStatus}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {stageDef.sub_statuses.map((ss) => (
                    <SelectItem key={ss.key || '__empty'} value={ss.key || '__empty'}>
                      {ss.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {(reasonRequired || isClosing) && (
            <div>
              <label className="text-xs text-slate-600">
                Reason{reasonRequired && <span className="text-red-500"> *</span>}
              </label>
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={isClosing ? 'Closing notes (audit trail)' : 'Why this transition?'}
              />
            </div>
          )}

          {isLost && (
            <>
              <div>
                <label className="text-xs text-slate-600">
                  Lost reason <span className="text-red-500">*</span>
                </label>
                <Select value={lostReason} onValueChange={setLostReason}>
                  <SelectTrigger><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                  <SelectContent>
                    {enums.lost_reasons.map((r) => (
                      <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {lostReason === 'other' && (
                <div>
                  <label className="text-xs text-slate-600">Detail</label>
                  <Input
                    value={lostNote}
                    onChange={(e) => setLostNote(e.target.value)}
                    placeholder="What happened? (sales coaching input)"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={isLost ? 'bg-red-600 hover:bg-red-500 text-white' : ''}
          >
            {submitting ? 'Saving…' : `Move to ${stageDef?.label}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- History timeline

export function LeadStageHistory({ leadId, token, limit = 50 }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!leadId || !token) return;
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/leads/${leadId}/stage-history?token=${token}`,
        );
        if (alive) setRows(res.data?.data?.history || []);
      } catch (_) {
        if (alive) setRows([]);
      }
    })();
    return () => { alive = false; };
  }, [leadId, token]);

  if (rows === null) return <div className="text-xs text-slate-400">Loading history…</div>;
  if (rows.length === 0) {
    return <div className="text-xs text-slate-400">No transitions logged yet.</div>;
  }
  return (
    <div className="space-y-2">
      {rows.slice(0, limit).map((h) => (
        <div key={h.id} className="border-l-2 border-slate-200 pl-3 py-1 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            {h.action === 'temperature' ? (
              <>
                <span className="font-medium">Temperature</span>
                <Badge variant="outline" className="text-[10px]">
                  {h.from_temperature || '—'} → {h.to_temperature}
                </Badge>
              </>
            ) : (
              <>
                <Badge variant="outline" className="text-[10px]">
                  {h.from_stage || '—'} → {h.to_stage}
                </Badge>
                {h.is_skip && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">skipped</Badge>}
                {h.is_backward && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">backward</Badge>}
              </>
            )}
            <span className="text-slate-400">
              by {h.actor_name} · {new Date(h.created_at).toLocaleString()}
            </span>
          </div>
          {h.reason && (
            <div className="text-slate-600 dark:text-slate-300 italic mt-0.5">"{h.reason}"</div>
          )}
          {h.lost_reason && (
            <div className="text-red-600 mt-0.5">
              Lost reason: {h.lost_reason}{h.lost_reason_note ? ` — ${h.lost_reason_note}` : ''}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- Convenience: helper to PATCH the temperature endpoint

export async function patchTemperature({ leadId, token, temperature, collection = 'auto' }) {
  await axios.patch(
    `${API}/leads/${leadId}/temperature?token=${token}`,
    { temperature, collection },
  );
}

// ---- Close-probability score badge + breakdown popover

const SCORE_TONE = (score) => {
  if (score >= 80) return 'bg-red-100 text-red-700 border-red-300';     // Hot
  if (score >= 60) return 'bg-orange-100 text-orange-700 border-orange-300'; // Warm
  if (score >= 40) return 'bg-blue-100 text-blue-700 border-blue-300';   // Cool
  return 'bg-slate-100 text-slate-700 border-slate-300';                 // Cold
};

/**
 * Closing-probability badge. Click to expand a breakdown of which
 * signals contributed to the score (component name → delta).
 * Manager can use the breakdown to coach an agent: "your lead is at
 * 45% because of a 14-day silence; reach out today".
 */
export function CloseProbabilityBadge({ leadId, token, collection = 'auto', compact = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!leadId || !token) return;
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/lead-intelligence/close-score/${leadId}`,
          { params: { token, collection } },
        );
        if (alive) setData(res.data?.data || null);
      } catch (_) {
        if (alive) setData({ score: null });
      }
    })();
    return () => { alive = false; };
  }, [leadId, token, collection]);

  if (!data || data.score === null || data.score === undefined) {
    return <Badge variant="outline" className="text-[10px]">No score</Badge>;
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border focus:outline-none ${
          SCORE_TONE(data.score)
        } ${compact ? 'text-[10px]' : 'text-xs'}`}
        title={`${data.score}% — ${data.label || ''}`}
      >
        <Flame size={11} />
        <strong>{data.score}%</strong>
        <span className="opacity-70">{data.label}</span>
      </button>
      {open && data.components && data.components.length > 0 && (
        <div className="absolute right-0 z-30 mt-1 w-72 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-3 text-xs">
          <div className="font-semibold mb-2">
            Why this score: <span className="text-slate-500">{data.score}% ({data.label})</span>
          </div>
          <div className="space-y-1">
            {data.components.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <span className="text-slate-600 dark:text-slate-300 capitalize">
                  {c.name.replace(/_/g, ' ')}
                </span>
                <span className={
                  c.delta > 0
                    ? 'text-emerald-700 font-mono'
                    : c.delta < 0
                      ? 'text-red-700 font-mono'
                      : 'text-slate-500 font-mono'
                }>
                  {c.delta > 0 ? '+' : ''}{c.delta}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            Heuristic score — pipeline stage, temperature, activity recency, stage aging, and oscillation are combined.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Best-agent suggestion popover. Used on the lead detail page when
 * the manager wants to (re)assign a lead. Pulls /suggest-agent and
 * shows up to 3 ranked candidates with their component breakdown.
 */
export function BestAgentSuggestions({ leadId, token, collection = 'auto', onAssign }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!leadId || !token) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/lead-intelligence/suggest-agent/${leadId}`,
        { params: { token, collection } },
      );
      setRows(res.data?.data?.suggestions || []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [leadId, token, collection]);

  useEffect(() => { load(); }, [load]);

  if (rows === null || loading) {
    return <div className="text-xs text-slate-400">Scoring agents…</div>;
  }
  if (rows.length === 0) {
    return <div className="text-xs text-slate-400">No agent suggestions available.</div>;
  }

  return (
    <div className="space-y-1.5">
      <p className="text-xs text-slate-500">Suggested agents (highest match first):</p>
      {rows.map((r, idx) => (
        <div key={r.agent_id} className="border border-slate-200 dark:border-slate-700 rounded p-2 text-xs">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">#{idx + 1} {r.agent_name}</span>
              <Badge className="text-[10px] bg-violet-100 text-violet-700 border-violet-300 border">
                {r.score} pts
              </Badge>
              <span className="text-slate-400">
                {r.won_leads}/{r.total_leads} won · {r.open_leads} open
              </span>
            </div>
            {onAssign && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => onAssign(r)}
              >
                Assign
              </Button>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] text-slate-500">
            {(r.components || []).map((c, i) => (
              <span key={i}>
                {c.name.replace(/_/g, ' ')}
                <span className={c.delta > 0 ? 'text-emerald-700' : c.delta < 0 ? 'text-red-700' : ''}>
                  {' '}{c.delta > 0 ? '+' : ''}{c.delta}
                </span>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
